// frontend/src/screens/ReceiptQueueContext.jsx
// Background queue for receipt uploads. Files are processed one at a time
// through: upload to S3 → OCR → parse → save pending. Survives tab switches.

import { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { transactionsApi, receiptsApi } from "../api/client";

// ── Image helpers ──────────────────────────────────────────────────────────
// Decode with EXIF orientation honored — phone photos often have rotation
// metadata that browsers and Tesseract ignore by default, producing sideways OCR.
const decodeWithOrientation = async (file) => {
  if (typeof createImageBitmap === "function") {
    try { return await createImageBitmap(file, { imageOrientation: "from-image" }); }
    catch { /* fall through */ }
  }
  // Fallback: standard Image element (orientation may be wrong on some browsers)
  return await new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload  = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Could not decode image")); };
    img.src = url;
  });
};

const resizeImage = async (file, maxWidth = 1600) => {
  const source = await decodeWithOrientation(file);
  let width  = source.width  || source.naturalWidth;
  let height = source.height || source.naturalHeight;
  if (width > maxWidth) {
    height = Math.round((height * maxWidth) / width);
    width = maxWidth;
  }
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  canvas.getContext("2d").drawImage(source, 0, 0, width, height);
  if (typeof source.close === "function") source.close();
  return await new Promise((resolve, reject) =>
    canvas.toBlob(b => b ? resolve(b) : reject(new Error("Resize failed")), "image/jpeg", 0.88)
  );
};

// ── OCR + regex parser ─────────────────────────────────────────────────────
const TESSERACT_CDN      = "https://cdn.jsdelivr.net/npm/tesseract.js@5.1.1/dist";
const TESSERACT_CORE_CDN = "https://cdn.jsdelivr.net/npm/tesseract.js-core@5.1.1";
const TESSDATA_CDN       = "https://tessdata.projectnaptha.com/4.0.0";

// Category inference — runs against the vendor name AND the full receipt text so
// item lines (TOOR DAL, KWALITY WALLS, ATTA, etc.) can tip the classification.
const VENDOR_TO_CATEGORY = [
  // Food — restaurants & food-delivery brands
  { match: /\b(zomato|swiggy|domino|pizza|restaurant|cafe|biryani|hotel|kitchen|udupi|sagar|saravana|mtr)\b/i, category: "Food" },
  // Food — supermarket / kirana / grocery vendors
  { match: /\b(supermarket|super\s*market|grocery|kirana|mart|big\s*bazaar|dmart|d-mart|reliance\s*fresh|more\s*super|nature.?s\s*basket|spencers|spar|easyday|vijetha|heritage\s*fresh|star\s*bazaar)\b/i, category: "Food" },
  // Food — common Indian grocery items (item-line matches on the receipt body)
  { match: /\b(toor\s*dal|moong\s*dal|chana\s*dal|urad\s*dal|masoor|atta|maida|basmati|rice\b|sona\s*masoori|idly|dosa|sambar|rasam|pickle|achaar|ghee|paneer|curd|dahi|jaggery|haldi|jeera|elaichi|cinnamon|garam\s*masala)\b/i, category: "Food" },
  // Food — major Indian food brands
  { match: /\b(amul|mother\s*dairy|britannia|parle|haldiram|maggi|kissan|tata\s*tea|brooke\s*bond|red\s*label|nescafe|bru|cadbury|nestle|kelloggs|kwality\s*wal|wall'?s|hatsun|aavin|gowardhan|md\s*dairy|heritage)\b/i, category: "Food" },
  // Food — generic + Indian snacks + common produce
  { match: /\b(milk|bread|biscuit|cookie|noodle|chocolate|ice[\s\-]?cream|vegetable|fruit|chicken|mutton|fish|eggs?|chudwa|chivda|kurmura|namkeen|murukku|sev|beans|cucumber|carrot|tomato|onion|potato|cabbage|spinach|brinjal|okra|coriander|ginger|garlic|lemon|lime|banana|apple|mango|orange|grape|english\s*c)\b/i, category: "Food" },
  // Structural signal — "Items/Qty" row is unique to retail receipts → almost always groceries
  { match: /\bItems\s*[\/\\]\s*[Q0]ty\b/i, category: "Food" },

  { match: /\b(uber|ola|rapido|metro|petrol|fuel|hp|bharat\s*petrol|indianoil|iocl|parking|toll)\b/i, category: "Transport" },
  { match: /\b(amazon|flipkart|myntra|ajio|nykaa|reliance\s*digital|croma)\b/i, category: "Clothing" },
  { match: /\b(apollo|medplus|pharmacy|hospital|clinic|diagnostic|lab|netmeds|1mg|pharmeasy|wellness|chemist)\b/i, category: "Healthcare" },
  { match: /\b(electricity|bescom|mseb|water\s*bill|gas|airtel|jio|vodafone|wifi|internet|broadband|tata\s*power|bsnl)\b/i, category: "Utilities" },
  { match: /\b(school|college|tuition|coaching|byju|unacademy|book|stationery)\b/i, category: "Education" },
  { match: /\b(movie|pvr|inox|netflix|prime|hotstar|spotify|concert|bookmyshow)\b/i, category: "Entertainment" },
  { match: /\b(salon|spa|haircut|grooming|beauty)\b/i, category: "Personal Care" },
  { match: /\b(donation|donations|charity|charitable|temple|gurudwara|gurdwara|mosque|church|trust\s+fund|ngo|relief\s+fund|orphanage|pradhan\s*mantri|pm\s*cares|akshaya\s*patra|tirumala|tirupati|sai\s*baba|ramakrishna|chinmaya|isha|art\s*of\s*living)\b/i, category: "Donation" },
  { match: /\b(loan|emi)\b/i, category: "EMI/Loan" },
  { match: /\b(insurance|premium|lic)\b/i, category: "Insurance" },
  { match: /\b(mutual\s*fund|sip|nps|ppf|zerodha|groww|kuvera|scripbox)\b/i, category: "Investment" },
  { match: /\brent\b/i, category: "Rent" },
];

const parseReceiptText = (text) => {
  const lines = text.split("\n").map(l => l.trim()).filter(Boolean);
  const joined = lines.join(" ");

  // Amount candidates — broad net then pick the largest "Total"-y match.
  const amountCandidates = [];

  // Pattern 1: explicit currency marker followed by a number
  //   "Rs 546.00", "Rs. 546", "₹546", "INR 546.00", "RS 546.00"
  for (const m of joined.matchAll(/(?:₹|Rs\.?|RS\.?|INR)\s*([\d,]+(?:\.\d{1,2})?)/gi)) {
    amountCandidates.push({ value: parseFloat(m[1].replace(/,/g, "")), score: 1, ctx: "currency" });
  }
  // Pattern 2: "Total/Grand Total/Amount/Net Payable" followed by a number (possibly with currency in between)
  //   "TOTAL RS 546.00", "Total: 546", "Grand Total 546.00", "NET PAYABLE: Rs.546"
  for (const m of joined.matchAll(/(?:Grand\s*Total|Net\s*Payable|Total|Amount)\s*[:\s]*\s*(?:Rs\.?|RS\.?|₹|INR)?\s*([\d,]+(?:\.\d{1,2})?)/gi)) {
    amountCandidates.push({ value: parseFloat(m[1].replace(/,/g, "")), score: 5, ctx: "total" });
  }
  // Pattern 3: numbers with two decimals — last-resort fallback for bare numeric receipts
  for (const m of joined.matchAll(/(?<![\d.])(\d{2,7}\.\d{2})(?![\d])/g)) {
    amountCandidates.push({ value: parseFloat(m[1]), score: 0, ctx: "decimal" });
  }

  const valid = amountCandidates.filter(c => !isNaN(c.value) && c.value > 0 && c.value < 10_000_000);
  // Prefer the highest-scored ("Total"-prefixed) candidate; tie-break on largest value (grand totals exceed line items)
  valid.sort((a, b) => b.score - a.score || b.value - a.value);
  const amount = valid[0]?.value ?? null;

  // Date extraction — prefer the receipt's labeled "Date:" / "Bill Date:" / "Invoice Date:"
  // field because that's where Indian receipts put the transaction date. Fall back to any
  // date-shaped string only if no labeled date is found.
  //
  // OCR commonly misreads digits → letters (O→0, I→1, l→1, S→5, B→8). Normalise inside dates.
  const fixOcrDigits = (s) => s.replace(/[OoQ]/g, "0").replace(/[IlL|]/g, "1").replace(/[Ss]/g, "5").replace(/[Bb]/g, "8");
  const months = ["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"];

  const parseDateParts = (dayRaw, monthRaw, yearRaw) => {
    const day = parseInt(fixOcrDigits(String(dayRaw)), 10);
    let month;
    const mTxt = String(monthRaw).trim();
    if (/^[a-z]+$/i.test(mTxt)) {
      month = months.indexOf(mTxt.toLowerCase().slice(0, 3)) + 1;
    } else {
      month = parseInt(fixOcrDigits(mTxt), 10);
    }
    let year = parseInt(fixOcrDigits(String(yearRaw)), 10);
    if (year < 100) year += 2000;
    if (day >= 1 && day <= 31 && month >= 1 && month <= 12 && year >= 2020 && year <= 2099) {
      return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    }
    return null;
  };

  // Try labeled patterns line-by-line first — much more reliable than free-text scan.
  // Examples we handle: "Date : 07-06-2026", "Date:07/06/2026", "Bill Date 07.06.2026",
  // "Invoice Date: 07-Jun-2026"
  const dateLabelRe = /(?:Date|Bill\s*Date|Invoice\s*Date|Bill\s*No.*Date|Issued\s*on)\s*[:\-]?\s*(\d{1,2})[\/\-\.\s](\d{1,2}|Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[\/\-\.\s](\d{2,4})/i;
  let txnDate = new Date().toISOString().slice(0, 10);
  let dateMatch = null;
  for (const line of lines) {
    const m = line.match(dateLabelRe);
    if (m) { dateMatch = m; break; }
  }
  if (!dateMatch) dateMatch = joined.match(dateLabelRe);
  // Last resort: any date-shaped substring anywhere
  if (!dateMatch) {
    dateMatch = joined.match(/\b(\d{1,2})[\/\-\.](\d{1,2}|Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[\/\-\.](\d{2,4})\b/i);
  }
  if (dateMatch) {
    const parsed = parseDateParts(dateMatch[1], dateMatch[2], dateMatch[3]);
    if (parsed) txnDate = parsed;
  }

  // Vendor selection — multi-pass fallback:
  // 1) Prefer a line containing a corporate suffix (PVT, LTD, etc.)
  // 2) Else, prefer the first "clean" looking line (mostly letters, not fragmented)
  // 3) Else, fall back to a category-derived label ("Grocery purchase" for receipts
  //    with an Items/Qty row) so the user sees something useful even when OCR is wrecked.
  const stripJunk = (l) => l.replace(/^[\s*~=\-_•·©®@|]+|[\s*~=\-_•·©®@|]+$/g, "").trim();
  const isJunkLine = (l) =>
    l.length <= 4 ||
    /^[\d\s.\-\/:,]+$/.test(l) ||
    /^(receipt|invoice|bill|tax|total|gst|cgst|sgst|amount|date|time|order|table|description|pos\b|hyd-|tssai|fssai|cin\b|pos\s*:)/i.test(l) ||
    /\b(order\s+on|home\s+delivery|live[- ]?\d*\s*m\b|free\s+delivery|cash\s+only|thank\s+you|visit\s+again|welcome|user\s*name|bill\s*[:#])/i.test(l) ||
    /\bdelivery!?\b/i.test(l) ||
    /^[^a-z]*$/i.test(l);
  // Reject OCR-shredded lines (too punctuation-heavy, too many single-letter words).
  const looksLikeName = (l) => {
    const letters = (l.match(/[a-zA-Z]/g) || []).length;
    if (letters < 5) return false;
    if (letters / l.length < 0.6) return false;
    const words = l.split(/\s+/);
    const singles = words.filter(w => w.length === 1).length;
    if (words.length > 4 && singles / words.length > 0.3) return false;
    return true;
  };

  const topLines = lines.slice(0, 12).map(stripJunk).filter(l => !isJunkLine(l));
  const corporate = topLines.find(l => looksLikeName(l) && /\b(pvt|private|ltd|limited|inc|llp|llc|company|co\.|corp|supermarket)\b/i.test(l));
  const clean = topLines.find(looksLikeName);
  const categoryGuess = (joined.match(/\bItems\s*[\/\\]\s*[Q0]ty\b/i)) ? "Grocery purchase" : null;
  const vendor = (corporate || clean || categoryGuess || "Receipt").slice(0, 80);

  // Category inference: check vendor first, then the ENTIRE receipt body
  // (item lines often live below the merchant header, so a 500-char window misses them).
  let category = "Other";
  for (const { match, category: cat } of VENDOR_TO_CATEGORY) {
    if (match.test(vendor) || match.test(joined)) { category = cat; break; }
  }

  let confidence = 0;
  if (amount) confidence += 0.5;
  if (dateMatch) confidence += 0.3;
  if (vendor && vendor !== "Receipt") confidence += 0.2;

  return { amount, txnDate, vendor, category, type: "expense", member: "Self", confidence, rawText: text.slice(0, 3000) };
};

// Hold a single worker instance — keeping it alive avoids re-downloading 10MB of language data per receipt.
let sharedWorker = null;
let workerLoadPromise = null;

const ensureWorker = async (onStatus) => {
  if (sharedWorker) return sharedWorker;
  if (workerLoadPromise) return workerLoadPromise;
  workerLoadPromise = (async () => {
    const { createWorker } = await import("tesseract.js");
    const w = await createWorker("eng", 1, {
      logger: (m) => {
        const friendly = {
          "loading tesseract core":     "Loading OCR engine…",
          "initializing tesseract":     "Starting engine…",
          "loading language traineddata": "Downloading English data…",
          "initializing api":           "Almost ready…",
          "recognizing text":           "Reading receipt…",
        }[m.status] || m.status || "Working…";
        onStatus?.({ text: friendly, progress: typeof m.progress === "number" ? Math.round(m.progress * 100) : null });
      },
      corePath:   TESSERACT_CORE_CDN,
      workerPath: `${TESSERACT_CDN}/worker.min.js`,
      langPath:   TESSDATA_CDN,
    });
    sharedWorker = w;
    return w;
  })();
  return workerLoadPromise;
};

// ── Context ──────────────────────────────────────────────────────────────
const ReceiptQueueContext = createContext(null);

export const useReceiptQueue = () => {
  const ctx = useContext(ReceiptQueueContext);
  if (!ctx) throw new Error("useReceiptQueue must be used inside ReceiptQueueProvider");
  return ctx;
};

let idCounter = 0;
const nextId = () => `item-${Date.now()}-${idCounter++}`;

const STATUS_LABELS = {
  queued:    "Queued",
  resizing:  "Preparing image",
  uploading: "Uploading to cloud",
  ocr:       "Reading text",
  parsing:   "Extracting fields",
  saving:    "Saving pending",
  done:      "Saved",
  failed:    "Failed",
};

export function ReceiptQueueProvider({ children }) {
  const [items, setItems] = useState([]);
  const [processing, setProcessing] = useState(false);
  const workerRef = useRef(false);   // guards against double-start

  const updateItem = useCallback((id, patch) => {
    setItems(prev => prev.map(it => it.id === id ? { ...it, ...patch } : it));
  }, []);

  const addFiles = useCallback((fileList) => {
    const files = Array.from(fileList).filter(f => f.type.startsWith("image/"));
    const newItems = files.map(f => ({
      id: nextId(),
      file: f,
      name: f.name || "receipt.jpg",
      size: f.size,
      status: "queued",
      statusText: STATUS_LABELS.queued,
      progress: 0,
      previewUrl: URL.createObjectURL(f),
      parsed: null,
      pendingTxnId: null,
      s3Key: null,
      error: null,
      addedAt: Date.now(),
    }));
    setItems(prev => [...prev, ...newItems]);
  }, []);

  const removeItem = useCallback((id) => {
    setItems(prev => {
      const item = prev.find(i => i.id === id);
      if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl);
      return prev.filter(i => i.id !== id);
    });
  }, []);

  const clearDone = useCallback(() => {
    setItems(prev => {
      prev.filter(i => i.status === "done" || i.status === "failed").forEach(i => {
        if (i.previewUrl) URL.revokeObjectURL(i.previewUrl);
      });
      return prev.filter(i => i.status !== "done" && i.status !== "failed");
    });
  }, []);

  // Background worker: processes one queued item at a time.
  useEffect(() => {
    if (workerRef.current) return;
    const queued = items.find(i => i.status === "queued");
    if (!queued) { setProcessing(false); return; }

    workerRef.current = true;
    setProcessing(true);

    (async () => {
      try {
        const id = queued.id;
        const file = queued.file;

        updateItem(id, { status: "resizing", statusText: STATUS_LABELS.resizing, progress: 0 });
        const blob = await resizeImage(file);

        updateItem(id, { status: "uploading", statusText: STATUS_LABELS.uploading, progress: 0 });
        const { key, url } = await receiptsApi.getUploadUrl(blob.type);
        await receiptsApi.uploadToS3(url, blob, (frac) => updateItem(id, { progress: Math.round(frac * 100) }));

        updateItem(id, { status: "ocr", statusText: STATUS_LABELS.ocr, progress: 0, s3Key: key });
        const worker = await ensureWorker((s) => updateItem(id, {
          statusText: s.text,
          ...(s.progress !== null ? { progress: s.progress } : {}),
        }));
        const { data } = await worker.recognize(blob);

        updateItem(id, { status: "parsing", statusText: STATUS_LABELS.parsing, progress: 0 });
        const parsed = parseReceiptText(data.text);

        updateItem(id, { status: "saving", statusText: STATUS_LABELS.saving, progress: 0, parsed });
        const txn = await transactionsApi.create({
          amount: parsed.amount || 0,
          type: parsed.type,
          category: parsed.category,
          member: parsed.member,
          txnDate: parsed.txnDate,
          note: parsed.vendor,
          status: "pending",
          source: "receipt",
          rawText: parsed.rawText,
          receiptS3Key: key,
        });

        updateItem(id, { status: "done", statusText: STATUS_LABELS.done, progress: 100, pendingTxnId: txn?.id || null });
      } catch (e) {
        console.error("Receipt processing failed:", e);
        updateItem(queued.id, {
          status: "failed",
          statusText: STATUS_LABELS.failed,
          error: e.message || "Unknown error",
        });
      } finally {
        workerRef.current = false;
      }
    })();
  }, [items, updateItem]);

  // Auto-clear successful items 5s after completion (gives user time to see ✓).
  useEffect(() => {
    const doneItems = items.filter(i => i.status === "done");
    if (doneItems.length === 0) return;
    const timeouts = doneItems.map(i => setTimeout(() => removeItem(i.id), 5000));
    return () => timeouts.forEach(clearTimeout);
  }, [items, removeItem]);

  const value = {
    items,
    processing,
    addFiles,
    removeItem,
    clearDone,
    counts: {
      total:    items.length,
      active:   items.filter(i => !["done", "failed"].includes(i.status)).length,
      done:     items.filter(i => i.status === "done").length,
      failed:   items.filter(i => i.status === "failed").length,
    },
  };

  return <ReceiptQueueContext.Provider value={value}>{children}</ReceiptQueueContext.Provider>;
}
